import React, { useRef, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';

const ResponsiveTabs = ({
  activeTab,
  onTabChange,
  tabs,
  variant = 'default',
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tabsRef = useRef(null);

  // Auto scroll active tab into view on desktop
  useEffect(() => {
    if (!isMobile && tabsRef.current) {
      const tabNode = tabsRef.current?.querySelector(
        '[aria-selected="true"]'
      );
      if (tabNode) {
        const tabsContainer = tabsRef.current.querySelector('[role="tablist"]');
        if (tabsContainer) {
          tabNode.scrollIntoView({ 
            behavior: 'smooth', 
            inline: 'center',
            block: 'nearest'
          });
        }
      }
    }
  }, [activeTab, isMobile]);

  const handleTabChange = (event, newValue) => {
    onTabChange(newValue);
  };

  const renderMobileDropdown = () => {
    if (!isMobile) return null;

    return (
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Select Section</InputLabel>
        <Select
          value={activeTab}
          onChange={(e) => onTabChange(e.target.value)}
          label="Select Section"
        >
          {tabs.map((tab, index) => (
            <MenuItem key={index} value={index}>
              {tab.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderDesktopTabs = () => {
    if (isMobile) return null;

    return (
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          ref={tabsRef}
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            display: { xs: 'none', sm: 'flex' },
            minHeight: 48,
            '& .MuiTabs-scrollButtons': {
              color: 'primary.main',
              '&.Mui-disabled': {
                opacity: 0.3,
              },
              '&:hover:not(.Mui-disabled)': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            },
            '& .MuiTabs-scrollable': {
              overflowX: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                height: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '2px',
                '&:hover': {
                  background: 'rgba(0, 0, 0, 0.3)',
                },
              },
            },
            '& .MuiTab-root': {
              minWidth: { sm: 120, md: 140 },
              fontSize: '0.85rem',
              fontWeight: 500,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              minHeight: 48,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
              '&.Mui-selected': {
                fontWeight: 600,
                color: 'primary.main',
              },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            ...sx,
          }}
          {...props}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition={tab.iconPosition || 'start'}
              disabled={tab.disabled}
            />
          ))}
        </Tabs>
      </Box>
    );
  };

  return (
    <Box>
      {renderMobileDropdown()}
      {renderDesktopTabs()}
    </Box>
  );
};

export default ResponsiveTabs;
